import { useEffect, useRef, useState } from "react";
import { useGetIdentity, useNotify, useRefresh, useUpdate } from "ra-core";
import { useLocation, useSearchParams } from "react-router";
import { Link2, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { getSupabaseClient } from "../providers/supabase/supabase";
import type { Company } from "../types";
import { cleanContextLinks } from "../misc/socialLinks";

// LDF: URL auf die Firmenseite ziehen → Website + Impressum auslesen → Felder zur Übernahme auswählen.

export type Enrichment = {
  url: string;
  name?: string;
  legal_name?: string;
  website?: string;
  description?: string;
  phone_number?: string;
  phone_numbers: { number: string; label: string }[];
  email?: string;
  address?: string;
  zipcode?: string;
  city?: string;
  country?: string;
  linkedin_url?: string;
  tax_identifier?: string;
  register?: string;
  logo_url?: string;
  context_links: string[];
  managing_directors: string[];
  pages: string[];
};

// Text aus der Zwischenablage als Website-Adresse deuten (mit oder ohne https://).
export const asWebsiteUrl = (text: string): string | null => {
  const t = text.trim();
  if (!t || /\s/.test(t) || t.length > 500 || t.includes("@")) return null;
  const candidate = /^https?:\/\//i.test(t) ? t : /^(www\.)?[a-z0-9-]+(\.[a-z0-9-]+)*\.[a-z]{2,}(\/.*)?$/i.test(t) ? `https://${t}` : null;
  if (!candidate) return null;
  try {
    return new URL(candidate).toString();
  } catch {
    return null;
  }
};

// Cmd+V irgendwo auf der Seite (außer in Eingabefeldern) liefert eine eingefügte URL.
export const usePastedUrl = (onUrl: (url: string) => void) => {
  const ref = useRef(onUrl);
  ref.current = onUrl;
  useEffect(() => {
    const handler = (e: ClipboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest("input, textarea, select, [contenteditable=true], [contenteditable='']")) return;
      const url = asWebsiteUrl(e.clipboardData?.getData("text/plain") ?? "");
      if (!url) return;
      e.preventDefault();
      ref.current(url);
    };
    document.addEventListener("paste", handler);
    return () => document.removeEventListener("paste", handler);
  }, []);
};

export const fetchEnrichment = async (url: string): Promise<Enrichment> => {
  const { data, error } = await getSupabaseClient().functions.invoke<Enrichment>("company-enrich", { body: { url } });
  if (error || !data) {
    let message = error?.message ?? "Abruf fehlgeschlagen";
    try {
      const body = await (error as { context?: Response })?.context?.json();
      if (body?.message) message = body.message;
    } catch {
      // Standardmeldung behalten
    }
    throw new Error(message);
  }
  return data;
};

type Row = { key: string; label: string; current: string; found: string };

const extractUrl = (e: React.DragEvent) => {
  const raw = e.dataTransfer.getData("text/uri-list") || e.dataTransfer.getData("text/plain");
  return raw.split(/\r?\n/).find((l) => l && !l.startsWith("#"))?.trim() ?? "";
};

const phoneKey = (n: string) => {
  let d = n.replace(/\(0\)/g, "").replace(/[^\d+]/g, "");
  if (d.startsWith("+")) d = d.slice(1);
  else if (d.startsWith("00")) d = d.slice(2);
  else if (d.startsWith("0")) d = "49" + d.slice(1);
  return d;
};

// Gefundene Nummern, die weder Hauptnummer noch schon hinterlegt sind
const newPhones = (record: Company, data: Enrichment) => {
  const known = new Set(
    [record.phone_number, ...(record.phone_numbers ?? []).map((p) => p.number)].filter(Boolean).map((n) => phoneKey(n!)),
  );
  const candidates = [
    ...(record.phone_number && data.phone_number ? [{ number: data.phone_number, label: "Telefon" }] : []),
    ...(data.phone_numbers ?? []),
  ];
  return candidates.filter((p) => !known.has(phoneKey(p.number)));
};

const buildRows = (record: Company, data: Enrichment): Row[] => {
  const impressum = [
    data.legal_name && data.legal_name !== record.name ? `Firmierung: ${data.legal_name}` : "",
    data.email ? `E-Mail: ${data.email}` : "",
    data.register ? `Register: ${data.register}` : "",
  ]
    .filter(Boolean)
    .join("\n");
  const rows: Row[] = [
    { key: "name", label: "Name", current: record.name ?? "", found: data.legal_name ?? data.name ?? "" },
    { key: "website", label: "Website", current: record.website ?? "", found: data.website ?? "" },
    { key: "phone_number", label: "Telefon", current: record.phone_number ?? "", found: data.phone_number ?? "" },
    {
      key: "phone_numbers",
      label: "Weitere Telefonnummern (ohne Fax)",
      current: (record.phone_numbers ?? []).map((p) => p.number).join("\n"),
      found: newPhones(record, data).map((p) => `${p.number}${p.label !== "Telefon" ? ` (${p.label})` : ""}`).join("\n"),
    },
    { key: "address", label: "Straße", current: record.address ?? "", found: data.address ?? "" },
    { key: "zipcode", label: "PLZ", current: record.zipcode ?? "", found: data.zipcode ?? "" },
    { key: "city", label: "Ort", current: record.city ?? "", found: data.city ?? "" },
    { key: "country", label: "Land", current: record.country ?? "", found: data.country ?? "" },
    { key: "linkedin_url", label: "LinkedIn", current: record.linkedin_url ?? "", found: data.linkedin_url ?? "" },
    { key: "tax_identifier", label: "USt-IdNr.", current: record.tax_identifier ?? "", found: data.tax_identifier ?? "" },
    { key: "description", label: "Beschreibung", current: record.description ?? "", found: data.description ?? "" },
    { key: "impressum", label: "Impressum-Angaben (an Beschreibung anhängen)", current: "", found: impressum },
    { key: "logo", label: "Logo", current: record.logo?.src ?? "", found: data.logo_url ?? "" },
    {
      key: "context_links",
      label: "Weitere Links",
      current: (record.context_links ?? []).join("\n"),
      found: cleanContextLinks([...(record.context_links ?? []), ...data.context_links], record.linkedin_url || data.linkedin_url)
        .filter((l) => !(record.context_links ?? []).includes(l))
        .join("\n"),
    },
  ];
  // Nur echte Unterschiede anzeigen, nicht bloße Schreibvarianten ("Str." / "Straße", "16/17" / "16-17")
  const same = (x: string, y: string) => {
    const norm = (v: string) =>
      v.toLowerCase().replace(/straße|strasse/g, "str").replace(/[^a-z0-9äöüß]/g, "");
    return norm(x) === norm(y);
  };
  return rows.filter((r) => r.found && r.found !== r.current && !(r.current && same(r.found, r.current)));
};

export const CompanyEnrich = ({ record }: { record: Company }) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const { identity } = useGetIdentity();
  const [update] = useUpdate();
  const [url, setUrl] = useState("");
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState<Enrichment | null>(null);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [people, setPeople] = useState<Record<string, boolean>>({});

  const rows = data ? buildRows(record, data) : [];
  const panelRef = useRef<HTMLDivElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  usePastedUrl((pasted) => {
    panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    load(pasted);
  });

  const location = useLocation();

  // Aufruf aus der Firmenliste: Ergebnis kommt über den Router-State mit, sonst ?enrich=<url>
  useEffect(() => {
    const passed = (location.state as { enrichment?: Enrichment } | null)?.enrichment;
    if (passed) {
      setUrl(passed.url);
      applyResult(passed);
      return;
    }
    const target = searchParams.get("enrich");
    if (!target) return;
    const next = new URLSearchParams(searchParams);
    next.delete("enrich");
    setSearchParams(next, { replace: true });
    load(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async (target: string) => {
    if (!target) return;
    setUrl(target);
    setLoading(true);
    setData(null);
    try {
      applyResult(await fetchEnrichment(target));
    } catch (e) {
      notify(`Website-Abruf: ${e instanceof Error ? e.message : String(e)}`, { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const applyResult = (result: Enrichment) => {
    setData(result);
    const initial: Record<string, boolean> = {};
    for (const row of buildRows(record, result)) {
      // Leere Felder vorauswählen, vorhandene Werte nur auf Wunsch überschreiben
      initial[row.key] = !row.current || ["context_links", "impressum", "phone_numbers"].includes(row.key);
    }
    setSelected(initial);
    setPeople(Object.fromEntries(result.managing_directors.map((n) => [n, true])));
  };

  const save = async () => {
    if (!data) return;
    setSaving(true);
    const values: Partial<Company> = {};
    for (const row of rows) {
      if (!selected[row.key]) continue;
      if (row.key === "logo") values.logo = { src: row.found, title: record.name } as Company["logo"];
      else if (row.key === "context_links")
        values.context_links = cleanContextLinks([...(record.context_links ?? []), ...row.found.split("\n")], record.linkedin_url || data.linkedin_url);
      else if (row.key === "impressum") continue;
      else if (row.key === "phone_numbers") values.phone_numbers = [...(record.phone_numbers ?? []), ...newPhones(record, data)];
      else (values as Record<string, string>)[row.key] = row.found;
    }
    if (selected.impressum) {
      const base = (values.description ?? record.description ?? "").trim();
      const block = rows.find((r) => r.key === "impressum")?.found ?? "";
      values.description = base ? `${base}\n\n${block}` : block;
    }

    try {
      if (Object.keys(values).length) {
        await update("companies", { id: record.id, data: values, previousData: record }, { returnPromise: true });
      }
      const names = Object.entries(people).filter(([, on]) => on).map(([n]) => n);
      if (names.length) {
        const supabase = getSupabaseClient();
        const { data: existing } = await supabase.from("contacts").select("first_name,last_name").eq("company_id", record.id);
        const known = new Set((existing ?? []).map((c) => `${c.first_name} ${c.last_name}`.toLowerCase()));
        const now = new Date().toISOString();
        const inserts = names
          .filter((n) => !known.has(n.toLowerCase()))
          .map((n) => {
            const parts = n.split(" ");
            return {
              first_name: parts.slice(0, -1).join(" "),
              last_name: parts.at(-1),
              title: "Geschäftsführung",
              company_id: record.id,
              sales_id: identity?.id ?? null,
              first_seen: now,
              last_seen: now,
              tags: [],
              email_jsonb: [],
              phone_jsonb: [],
              has_newsletter: false,
              background: `Laut Impressum ${data.pages.at(-1) ?? data.url} (abgerufen ${new Date().toLocaleDateString("de-DE")})`,
            };
          });
        if (inserts.length) {
          const { error } = await supabase.from("contacts").insert(inserts);
          if (error) throw error;
        }
      }
      notify("Firmendaten übernommen", { type: "success" });
      setData(null);
      setUrl("");
      refresh();
    } catch (e) {
      notify(`Speichern fehlgeschlagen: ${e instanceof Error ? e.message : String(e)}`, { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      ref={panelRef}
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        load(extractUrl(e));
      }}
      className={`rounded-lg border border-dashed p-3 text-sm transition-colors ${dragging ? "border-primary bg-primary/10" : "border-border"}`}
    >
      <div className="mb-2 flex items-center gap-2 text-muted-foreground">
        <Link2 className="h-4 w-4" />
        <span>Website-Link mit Cmd+V einfügen oder hierher ziehen</span>
      </div>
      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          load(url.trim());
        }}
      >
        <input
          type="text"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder={record.website || "https://…"}
          className="h-8 min-w-0 flex-1 rounded-md border border-input bg-background px-2 text-sm"
        />
        <Button type="submit" size="sm" disabled={loading || !url.trim()}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Daten holen"}
        </Button>
      </form>
      {!url && record.website && !data && !loading && (
        <button type="button" className="mt-2 text-xs underline cursor-pointer" onClick={() => load(record.website)}>
          Hinterlegte Website auslesen
        </button>
      )}

      {data && (
        <div className="mt-3 space-y-3">
          <p className="text-xs text-muted-foreground">
            Gelesen: {data.pages.map((p) => new URL(p).pathname || "/").join(", ")}. Bitte prüfen, was übernommen werden soll.
          </p>
          {rows.length === 0 && !data.managing_directors.length && (
            <p className="text-xs">Keine neuen Angaben gefunden.</p>
          )}
          {rows.map((row) => (
            <label key={row.key} className="flex items-start gap-2">
              <input
                type="checkbox"
                className="mt-1"
                checked={!!selected[row.key]}
                onChange={(e) => setSelected({ ...selected, [row.key]: e.target.checked })}
              />
              <span className="min-w-0 flex-1">
                <span className="block text-xs font-medium">{row.label}</span>
                {row.key === "logo" ? (
                  <img src={row.found} alt="" className="mt-1 h-10 max-w-full object-contain" />
                ) : (
                  <span className="block whitespace-pre-line break-words text-xs">{row.found}</span>
                )}
                {row.current && row.key !== "logo" && (
                  <span className="block break-words text-xs text-muted-foreground line-through">
                    {row.current.slice(0, 160)}
                  </span>
                )}
              </span>
            </label>
          ))}
          {data.managing_directors.length > 0 && (
            <div>
              <span className="block text-xs font-medium">Geschäftsführung als Kontakt anlegen</span>
              {data.managing_directors.map((n) => (
                <label key={n} className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={!!people[n]}
                    onChange={(e) => setPeople({ ...people, [n]: e.target.checked })}
                  />
                  {n}
                </label>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <Button size="sm" onClick={save} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Übernehmen"}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setData(null)}>
              Verwerfen
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
